import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Main from '../layouts/Main';
import { useApi } from '../contexts/Api';
import CoefficientRuleList from '../components/CoefficientRuleList';
import AddCoefficientRuleDialog from '../components/AddCoefficientRuleDialog';
import EditCoefficientRuleDialog from '../components/EditCoefficientRuleDialog';

const CoefficientRules = () => {
  const { coefficientRules, loadingCoefficientRules, fetchCoefficientRules } = useApi();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);

  const handleAddClick = () => setAddDialogOpen(true);
  const handleEditClick = (rule) => { setSelectedRule(rule); setEditDialogOpen(true); };
  const handleClose = () => {
    setAddDialogOpen(false);
    setEditDialogOpen(false);
    setSelectedRule(null);
  };
  const handleAddSave = async () => { setAddDialogOpen(false); await fetchCoefficientRules(); };
  const handleEditSave = async () => { setEditDialogOpen(false); setSelectedRule(null); await fetchCoefficientRules(); };

  const hasCatchAll = (coefficientRules || []).some(r =>
    r.active == 1 && r.min_price == null && r.max_price == null
  );

  return (
    <Main>
      <div className="page-header">
        <div>
          <h1 className="page-title">Coefficient Rules</h1>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active" aria-current="page">Coefficient Rules</li>
          </ol>
        </div>
        <div className="page-header-actions">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{ backgroundColor: '#845adf', '&:hover': { backgroundColor: '#6c48c5' } }}
          >
            Add Rule
          </Button>
        </div>
      </div>

      {!loadingCoefficientRules && coefficientRules.length > 0 && !hasCatchAll && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>No catch-all rule.</strong> If a product's base price doesn't match any rule, the system falls back to its per-product coefficient (which may be 0 for new products). Add a rule with no price range as a safety net.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Rules are evaluated top-down by priority. The first rule whose price range contains the product's <strong>base price</strong> sets the markup coefficient. Drag rows to reorder.
      </Alert>

      <div className="row">
        <div className="col-12">
          <div className="card custom-card">
            <div className="card-header">
              <div className="card-title">All Coefficient Rules</div>
            </div>
            <div className="card-body">
              {loadingCoefficientRules ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                  <CircularProgress />
                </Box>
              ) : coefficientRules && coefficientRules.length > 0 ? (
                <CoefficientRuleList
                  rules={coefficientRules}
                  onEdit={handleEditClick}
                  onRefresh={fetchCoefficientRules}
                />
              ) : (
                <Box textAlign="center" py={5}>
                  <Typography variant="h6" color="text.secondary">
                    No coefficient rules yet. Add one to start computing sell prices.
                  </Typography>
                </Box>
              )}
            </div>
          </div>
        </div>
      </div>

      <AddCoefficientRuleDialog open={addDialogOpen} onClose={handleClose} onSave={handleAddSave} />
      <EditCoefficientRuleDialog open={editDialogOpen} onClose={handleClose} rule={selectedRule} onSave={handleEditSave} />
    </Main>
  );
};

export default CoefficientRules;
